// ---------------------------------------------------------------------------
// Survive on BCH — Game Engine (pure functions)
//
// All computations here operate on SIMULATED values only.
// ---------------------------------------------------------------------------

import {
  BCH_FEE_USD,
  BTC_LIGHTNING_FEE_USD,
  MARKETS,
  STARTING_CRYPTO_USD,
  STARTING_FIAT_USD,
} from "./data";
import type { DayMarket } from "./types";
import type {
  Decision,
  GameMode,
  PaymentMethod,
  RunResult,
  Scenario,
  ScenarioFlow,
  ScenarioOption,
} from "./types";

/** A scenario's flow, defaulting to "spend" for backwards compatibility. */
export function scenarioFlow(scenario: Scenario): ScenarioFlow {
  return scenario.flow ?? "spend";
}

/** Does the merchant / payer accept the active coin for this option? */
export function acceptsCoin(option: ScenarioOption, mode: GameMode): boolean {
  if (option.method !== "crypto") return true;
  return mode === "BCH" ? option.acceptsBch !== false : option.acceptsBtc === true;
}

/** Look up the market context for a day (falls back to day 1). */
export function marketForDay(day: number): DayMarket {
  return MARKETS.find((m) => m.day === day) ?? MARKETS[0];
}

/**
 * Simulated fee for a specific option, mode, and DAY.
 *  - BCH: always the flat, near-zero on-chain fee (no fee market).
 *  - BTC over Lightning (btcLightning merchants): tiny Lightning fee.
 *  - BTC on-chain: that day's on-chain fee, which spikes when congested.
 */
export function feeForOption(
  option: ScenarioOption,
  mode: GameMode,
  day: number,
): number {
  if (mode === "BCH") return BCH_FEE_USD;
  if (option.btcLightning) return BTC_LIGHTNING_FEE_USD;
  return marketForDay(day).btcOnchainFeeUsd;
}

/** Was this option's fee inflated by a BTC congestion spike that day? */
export function feeIsSpiked(
  option: ScenarioOption,
  mode: GameMode,
  day: number,
): boolean {
  if (mode !== "BTC" || option.btcLightning) return false;
  return marketForDay(day).btcCongested;
}

/** Discounted USD price for an option in the active mode. */
export function effectivePrice(option: ScenarioOption, mode: GameMode): number {
  if (option.method === "crypto" && acceptsCoin(option, mode) && option.cryptoDiscount) {
    return round2(option.priceUsd * (1 - option.cryptoDiscount));
  }
  return round2(option.priceUsd);
}

/** Compute a Decision record from a chosen option, without mutating state. */
export function resolveDecision(
  scenario: Scenario,
  option: ScenarioOption,
  mode: GameMode,
): Decision {
  return scenarioFlow(scenario) === "earn"
    ? resolveEarnDecision(scenario, option, mode)
    : resolveSpendDecision(scenario, option, mode);
}

/** Resolve a "money out" decision (the original purchase logic). */
function resolveSpendDecision(
  scenario: Scenario,
  option: ScenarioOption,
  mode: GameMode,
): Decision {
  const accepted = acceptsCoin(option, mode);
  const usingCrypto = option.method === "crypto" && accepted;

  const baseCost = option.priceUsd;
  const cost = usingCrypto && option.cryptoDiscount
    ? round2(baseCost * (1 - option.cryptoDiscount))
    : round2(baseCost);
  const saved = usingCrypto && option.cryptoDiscount ? round2(baseCost - cost) : 0;
  const fee = usingCrypto && baseCost > 0 ? feeForOption(option, mode, scenario.day) : 0;
  const feeSpiked = usingCrypto && baseCost > 0 && feeIsSpiked(option, mode, scenario.day);

  // If the player tried to pay with crypto but the merchant doesn't accept the
  // coin, they fall back to fiat at full price (no discount) with a wellbeing hit.
  let wellbeing = option.wellbeing;
  let outcome = option.outcome;
  if (option.method === "crypto" && !accepted) {
    wellbeing = option.wellbeing - 6;
    const otherCoin = mode === "BCH" ? "BTC" : "BCH";
    outcome = `This merchant only takes ${otherCoin} here, not ${mode}, so you paid full price with fiat instead.`;
  }

  // Getting hit with a painful fee spike is frustrating, not just costly.
  if (feeSpiked && fee >= 5) {
    wellbeing -= 4;
    outcome = `${outcome} Ouch — the BTC network was congested, so this cost ${formatUsd(fee)} in fees alone.`;
  }

  return {
    day: scenario.day,
    scenarioId: scenario.id,
    scenarioTitle: scenario.title,
    category: scenario.category,
    flow: "spend",
    optionId: option.id,
    optionLabel: option.label,
    method: usingCrypto ? "crypto" : "fiat",
    accepted,
    costUsd: cost,
    earnedUsd: 0,
    feeUsd: fee,
    feeSpiked,
    savedUsd: saved,
    wellbeing,
    outcome,
  };
}

/**
 * Resolve a "money in" decision. Getting paid is where the coins differ just as
 * much as spending:
 *  - Crypto payout: the payer's network fee comes OUT of what you receive.
 *    In BCH that's a fraction of a cent; a BTC on-chain payout on a congested
 *    day can shave real money off a small gig.
 *  - Fiat payout: no network fee, but a service cut skims a percentage and the
 *    money settles a day or two later.
 */
function resolveEarnDecision(
  scenario: Scenario,
  option: ScenarioOption,
  mode: GameMode,
): Decision {
  const accepted = acceptsCoin(option, mode);
  const usingCrypto = option.method === "crypto" && accepted;

  const gross = option.priceUsd;

  // Declining the income (skip) moves no money.
  if (option.skip) {
    return baseEarnDecision(scenario, option, mode, {
      method: "fiat",
      accepted: true,
      earned: 0,
      fee: 0,
      feeSpiked: false,
      lostToCut: 0,
      wellbeing: option.wellbeing,
      outcome: option.outcome,
    });
  }

  // Crypto payout that the payer can't actually send in your coin: you fall
  // back to being paid in fiat, taking a typical service cut + a wellbeing hit.
  if (option.method === "crypto" && !accepted) {
    const fallbackCut = option.fiatCut ?? 0.05;
    const lost = round2(gross * fallbackCut);
    const otherCoin = mode === "BCH" ? "BTC" : "BCH";
    return baseEarnDecision(scenario, option, mode, {
      method: "fiat",
      accepted: false,
      earned: round2(gross - lost),
      fee: 0,
      feeSpiked: false,
      lostToCut: lost,
      wellbeing: option.wellbeing - 6,
      outcome: `The payer could only send ${otherCoin} here, not ${mode}, so you took it in fiat instead — a ${Math.round(
        fallbackCut * 100,
      )}% service cut ate ${formatUsd(lost)}.`,
    });
  }

  if (usingCrypto) {
    const fee = gross > 0 ? feeForOption(option, mode, scenario.day) : 0;
    const feeSpiked = gross > 0 && feeIsSpiked(option, mode, scenario.day);
    let wellbeing = option.wellbeing;
    let outcome = option.outcome;
    if (feeSpiked && fee >= 5) {
      wellbeing -= 4;
      outcome = `${outcome} Ouch — BTC was congested, so ${formatUsd(
        fee,
      )} got shaved off your payout in network fees.`;
    }
    return baseEarnDecision(scenario, option, mode, {
      method: "crypto",
      accepted: true,
      earned: round2(gross - fee),
      fee,
      feeSpiked,
      lostToCut: 0,
      wellbeing,
      outcome,
    });
  }

  // Plain fiat payout with a service cut and settlement delay.
  const cut = option.fiatCut ?? 0;
  const lost = round2(gross * cut);
  const settle = option.settleDays ?? 0;
  const wellbeing = option.wellbeing - (settle >= 2 ? 2 : 0);
  return baseEarnDecision(scenario, option, mode, {
    method: "fiat",
    accepted: true,
    earned: round2(gross - lost),
    fee: 0,
    feeSpiked: false,
    lostToCut: lost,
    wellbeing,
    outcome: option.outcome,
  });
}

interface EarnResult {
  method: PaymentMethod;
  accepted: boolean;
  earned: number;
  fee: number;
  feeSpiked: boolean;
  lostToCut: number;
  wellbeing: number;
  outcome: string;
}

function baseEarnDecision(
  scenario: Scenario,
  option: ScenarioOption,
  _mode: GameMode,
  r: EarnResult,
): Decision {
  return {
    day: scenario.day,
    scenarioId: scenario.id,
    scenarioTitle: scenario.title,
    category: scenario.category,
    flow: "earn",
    optionId: option.id,
    optionLabel: option.label,
    method: r.method,
    accepted: r.accepted,
    costUsd: 0,
    earnedUsd: r.earned,
    feeUsd: r.fee,
    feeSpiked: r.feeSpiked,
    savedUsd: r.lostToCut,
    wellbeing: r.wellbeing,
    outcome: r.outcome,
  };
}

/** Build an empty run for a mode. */
export function emptyRun(mode: GameMode): RunResult {
  return {
    mode,
    startingFiatUsd: STARTING_FIAT_USD,
    startingCryptoUsd: STARTING_CRYPTO_USD,
    remainingFiatUsd: STARTING_FIAT_USD,
    remainingCryptoUsd: STARTING_CRYPTO_USD,
    totalSpentUsd: 0,
    totalEarnedUsd: 0,
    totalFeesUsd: 0,
    spikeFeesUsd: 0,
    totalSavedUsd: 0,
    wellbeing: 50,
    survived: true,
    score: 0,
    decisions: [],
  };
}

/** Wellbeing penalty per dollar you couldn't cover when truly broke. */
const SHORTFALL_WELLBEING_PER_USD = 0.5;
/** Cap the pain of a single unaffordable purchase so one miss isn't fatal. */
const MAX_SHORTFALL_WELLBEING = 20;

export interface SpendResult {
  /** New fiat balance after the payment (never below 0). */
  remainingFiatUsd: number;
  /** New crypto balance after the payment (never below 0). */
  remainingCryptoUsd: number;
  /**
   * Amount that had to be drawn from the OTHER wallet because the chosen one
   * couldn't cover the whole purchase. 0 when the chosen wallet was enough.
   */
  dippedIntoOtherUsd: number;
  /** Amount you couldn't cover at all (truly broke). 0 when fully paid. */
  shortfallUsd: number;
  /** Extra wellbeing lost because of an unaffordable purchase (<= 0). */
  wellbeingPenalty: number;
}

/**
 * Compute the result of spending `spend` USD, charging the CHOSEN wallet first
 * and only dipping into the other wallet if the chosen one runs short. If the
 * player's TOTAL money still can't cover it, the remainder is a shortfall that
 * costs wellbeing (you went without) rather than silently going negative.
 */
export function computeSpend(
  remainingFiatUsd: number,
  remainingCryptoUsd: number,
  spend: number,
  method: PaymentMethod,
): SpendResult {
  let fiat = remainingFiatUsd;
  let crypto = remainingCryptoUsd;
  let dipped = 0;

  // Which wallet is charged first depends on how the player chose to pay.
  const chargeOrder: ("fiat" | "crypto")[] =
    method === "crypto" ? ["crypto", "fiat"] : ["fiat", "crypto"];

  let remaining = spend;
  for (let i = 0; i < chargeOrder.length; i++) {
    if (remaining <= 0) break;
    const wallet = chargeOrder[i];
    const available = wallet === "fiat" ? fiat : crypto;
    const take = Math.min(available, remaining);
    if (wallet === "fiat") fiat = round2(fiat - take);
    else crypto = round2(crypto - take);
    remaining = round2(remaining - take);
    // Anything paid from the SECOND wallet counts as "dipping into" it.
    if (i > 0) dipped = round2(dipped + take);
  }

  const shortfall = round2(Math.max(0, remaining));
  const wellbeingPenalty = shortfall > 0
    ? -Math.min(MAX_SHORTFALL_WELLBEING, round2(shortfall * SHORTFALL_WELLBEING_PER_USD))
    : 0;

  return {
    remainingFiatUsd: fiat,
    remainingCryptoUsd: crypto,
    dippedIntoOtherUsd: dipped,
    shortfallUsd: shortfall,
    wellbeingPenalty,
  };
}

/** Apply a decision to a run, returning a NEW run object. */
export function applyDecision(run: RunResult, decision: Decision): RunResult {
  const next: RunResult = {
    ...run,
    decisions: [...run.decisions, decision],
  };

  if (decision.flow === "earn") {
    // Income lands in the wallet it was received into. Crypto payouts credit
    // the crypto balance (fee already netted out of earnedUsd); fiat payouts
    // credit the fiat balance.
    if (decision.method === "crypto") {
      next.remainingCryptoUsd = round2(next.remainingCryptoUsd + decision.earnedUsd);
    } else {
      next.remainingFiatUsd = round2(next.remainingFiatUsd + decision.earnedUsd);
    }
    next.totalEarnedUsd = round2(next.totalEarnedUsd + decision.earnedUsd);
    next.totalFeesUsd = round2(next.totalFeesUsd + decision.feeUsd);
    if (decision.feeSpiked) {
      next.spikeFeesUsd = round2(next.spikeFeesUsd + decision.feeUsd);
    }
    next.wellbeing = clamp(next.wellbeing + decision.wellbeing, 0, 100);
    return next;
  }

  const spend = round2(decision.costUsd + decision.feeUsd);

  // Charge the chosen wallet first, fall back to the other, and take a
  // wellbeing hit for anything the player genuinely couldn't afford.
  const result = computeSpend(
    next.remainingFiatUsd,
    next.remainingCryptoUsd,
    spend,
    decision.method,
  );
  next.remainingFiatUsd = result.remainingFiatUsd;
  next.remainingCryptoUsd = result.remainingCryptoUsd;

  next.totalSpentUsd = round2(next.totalSpentUsd + decision.costUsd);
  next.totalFeesUsd = round2(next.totalFeesUsd + decision.feeUsd);
  if (decision.feeSpiked) {
    next.spikeFeesUsd = round2(next.spikeFeesUsd + decision.feeUsd);
  }
  next.totalSavedUsd = round2(next.totalSavedUsd + decision.savedUsd);
  next.wellbeing = clamp(
    next.wellbeing + decision.wellbeing + result.wellbeingPenalty,
    0,
    100,
  );

  return next;
}

/**
 * Apply simulated market drift to the crypto balance between days.
 * The remaining crypto (USD terms) scales with the coin's price change.
 */
export function applyMarketDrift(
  run: RunResult,
  fromDay: number,
  toDay: number,
): RunResult {
  const from = MARKETS.find((m) => m.day === fromDay);
  const to = MARKETS.find((m) => m.day === toDay);
  if (!from || !to) return run;

  const ratio = to.priceUsd[run.mode] / from.priceUsd[run.mode];
  return {
    ...run,
    remainingCryptoUsd: round2(run.remainingCryptoUsd * ratio),
  };
}

/** Finalize a run: compute survival + score. */
export function finalizeRun(run: RunResult): RunResult {
  const totalRemaining = run.remainingFiatUsd + run.remainingCryptoUsd;
  // Wallets are floored at $0 during play (unaffordable purchases cost
  // wellbeing instead of going negative), so running out of BOTH money and
  // wellbeing is what ends the week badly.
  const survived = run.wellbeing > 0;

  // Score blends wellbeing, money kept, and fees avoided (all simulated).
  const moneyScore = clamp((totalRemaining / (STARTING_FIAT_USD + STARTING_CRYPTO_USD)) * 100, 0, 100);
  const feePenalty = clamp(run.totalFeesUsd * 1.2, 0, 35);
  const savingsBonus = clamp(run.totalSavedUsd, 0, 30);

  const score = Math.round(
    run.wellbeing * 0.5 + moneyScore * 0.4 + savingsBonus - feePenalty + (survived ? 10 : 0),
  );

  return {
    ...run,
    survived,
    score: Math.max(0, score),
  };
}

/** Convert a USD amount to a simulated coin amount for display. */
export function usdToCoin(usd: number, mode: GameMode, day: number): number {
  const market = MARKETS.find((m) => m.day === day) ?? MARKETS[0];
  return usd / market.priceUsd[mode];
}

/** Format a simulated coin amount with sensible precision. */
export function formatCoin(amount: number, mode: GameMode): string {
  if (mode === "BTC") {
    return `${amount.toFixed(6)} BTC`;
  }
  return `${amount.toFixed(4)} BCH`;
}

export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/**
 * Format a fee honestly: sub-cent fees show as "<$0.01" rather than
 * rounding down to "$0.00".
 */
export function formatFee(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return "<$0.01";
  return `$${usd.toFixed(2)}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
